import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoutIcon from '@mui/icons-material/Logout';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PaletteMode } from '@mui/material';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarIcon from '@mui/icons-material/Star';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import api from '../services/api';

// カスタムSVGアイコン（デザインSVGから抽出）
import { ReactComponent as HomeNavIcon } from '../assets/icons/home-nav.svg';
import { ReactComponent as JobManagementNavIcon } from '../assets/icons/job-management-nav.svg';
import { ReactComponent as CrewManagementNavIcon } from '../assets/icons/crew-management-nav.svg';
import { ReactComponent as ApprovalNavIcon } from '../assets/icons/approval-nav.svg';
import { ReactComponent as RecruitFabIcon } from '../assets/icons/recruit-fab.svg';
import { ReactComponent as HeaderMenuIcon } from '../assets/icons/header-menu-nav.svg';
import { ReactComponent as HeaderBellIcon } from '../assets/icons/header-bell-nav.svg';
import { ReactComponent as HeaderCalendarIcon } from '../assets/icons/header-calendar-nav.svg';
import { ReactComponent as HeaderQrIcon } from '../assets/icons/header-qr-nav.svg';

// サイドメニュー用SVGアイコン（メニュー.svgから抽出）
import { ReactComponent as SidebarHomeIcon } from '../assets/icons/sidebar_home.svg';
import { ReactComponent as SidebarJobMgmtIcon } from '../assets/icons/sidebar_job_management.svg';
import { ReactComponent as SidebarJobTemplateIcon } from '../assets/icons/sidebar_job_template.svg';
import { ReactComponent as SidebarCrewMgmtIcon } from '../assets/icons/sidebar_crew_management.svg';
import { ReactComponent as SidebarDashboardIcon } from '../assets/icons/sidebar_dashboard.svg';
import { ReactComponent as SidebarNotifIcon } from '../assets/icons/sidebar_notification.svg';
import { ReactComponent as SidebarStoreIcon } from '../assets/icons/sidebar_store_settings.svg';
import { ReactComponent as SidebarUserIcon } from '../assets/icons/sidebar_user_settings.svg';
import { ReactComponent as SidebarLineNotifIcon } from '../assets/icons/sidebar_line_notification.svg';
import { ReactComponent as SidebarBillingIcon } from '../assets/icons/sidebar_billing.svg';
import { ReactComponent as SidebarFeedbackIcon } from '../assets/icons/sidebar_feedback.svg';
import { ReactComponent as SidebarPrivacyIcon } from '../assets/icons/sidebar_privacy_policy.svg';

// 認証関連
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// ブランドカラー（デザインSVGより）
const BRAND_COLOR = '#0071e3';
const BRAND_COLOR_HOVER = '#0062c4';

const drawerWidth = 260;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  backgroundColor: BRAND_COLOR,
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface LayoutProps {
  children: React.ReactNode;
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, mode, toggleColorMode }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // お知らせ詳細画面（/announcements/:id）でもお知らせメニューをアクティブにする
  const isMenuActive = (menuPath: string) => {
    if (location.pathname === menuPath) return true;
    if (menuPath === '/notifications' && location.pathname.startsWith('/announcements/')) return true;
    // クルー詳細（/crew-management/:storeUserId）でもクルー管理メニューをアクティブにする
    if (menuPath === '/crew-management' && location.pathname.startsWith('/crew-management/')) return true;
    return false;
  };
  const [open, setOpen] = useState(() => localStorage.getItem('sidebar_open') === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  
  // 認証コンテキストからユーザー情報とログアウト関数を取得
  const { signOut, isAdmin, currentUser, storeId, storeIds, storeName, switchStore } = useAuth();
  // const isNotificationsPage = location.pathname === '/notifications' || location.pathname.startsWith('/announcements/');

  // Firebase認証済みでManagementAccount未ログインの場合、emailで自動ログインを試みる
  const { isManagementUser, managementAccount, tryAutoLogin } = useManagementAuth(); 
  const autoLoginAttempted = useRef(false);
  useEffect(() => {
    if (currentUser?.email && !isManagementUser && !autoLoginAttempted.current) {
      autoLoginAttempted.current = true;
      tryAutoLogin(currentUser.email);
    }
  }, [currentUser?.email, isManagementUser, tryAutoLogin]);
  

  // 店舗切り替えメニュー
  const [storeMenuAnchor, setStoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [storeNameMap, setStoreNameMap] = useState<Record<string, string>>({});
  const hasMultipleStores = storeIds.length > 1;

  // 複数店舗がある場合、全店舗名を取得
  const fetchAllStoreNames = useCallback(async () => {
    if (storeIds.length <= 1) return;
    const nameMap: Record<string, string> = {};
    await Promise.all(
      storeIds.map(async (id) => {
        try {
          const info = await api.store.getStoreInfo(id);
          nameMap[id] = info.name || id;
        } catch {
          nameMap[id] = id;
        }
      })
    );
    setStoreNameMap(nameMap);
  }, [storeIds]);

  useEffect(() => {
    fetchAllStoreNames();
  }, [fetchAllStoreNames]);

  const handleStoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (hasMultipleStores) {
      setStoreMenuAnchor(event.currentTarget);
    }
  };

  const handleStoreMenuClose = () => {
    setStoreMenuAnchor(null);
  };

  const handleStoreSwitch = (newStoreId: string) => {
    switchStore(newStoreId);
    handleStoreMenuClose();
  };

  const handleDrawerOpen = () => {
    setOpen(true);
    localStorage.setItem('sidebar_open', 'true');
  };

  const handleDrawerClose = () => {
    setOpen(false);
    localStorage.setItem('sidebar_open', 'false');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // ログアウト処理
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
    }
  };

  // メニュー項目（PDFの仕様に合わせて15項目）
  // isAdmin(=is_superadmin) の場合は「店舗管理」を追加表示
  const menuItems = [
{ text: 'ホーム', icon: <SvgIcon component={SidebarHomeIcon} inheritViewBox />, path: '/' },
    { text: 'クルー管理', icon: <SvgIcon component={SidebarCrewMgmtIcon} inheritViewBox />, path: '/crew-management' },
    { text: '応募者管理', icon: <AssignmentIndIcon />, path: '/applicants' },
    { text: '求人管理', icon: <SvgIcon component={SidebarJobTemplateIcon} inheritViewBox />, path: '/job-management' },
    { text: '今すぐ募集', icon: <SvgIcon component={RecruitFabIcon} inheritViewBox />, path: '/line-broadcast' },
    { text: '求人ひな形', icon: <SvgIcon component={SidebarJobMgmtIcon} inheritViewBox />, path: '/job-templates' },
    { text: 'データ', icon: <SvgIcon component={SidebarDashboardIcon} inheritViewBox />, path: '/dashboard' },
    { text: '出退勤QR', icon: <HeaderQrIcon style={{ width: 24, height: 24 }} />, path: '/attendance/qr' },
    { text: '就業予定表', icon: <HeaderCalendarIcon style={{ width: 24, height: 24 }} />, path: '/attendance/schedule' },
    { text: '承認', icon: <ApprovalNavIcon style={{ width: 24, height: 24 }} />, path: '/attendance/approval' },
    { text: 'お知らせ', icon: <SvgIcon component={SidebarNotifIcon} inheritViewBox />, path: '/notifications' },
    { text: '店舗設定', icon: <SvgIcon component={SidebarStoreIcon} inheritViewBox />, path: '/settings/store' },
    { text: 'スキル設定', icon: <StarIcon />, path: '/skill-settings' },
    { text: 'ランク設定', icon: <StarIcon />, path: '/rank-settings' },
    { text: '特別オファーテンプレ', icon: <LocalOfferIcon />, path: '/special-offer-templates' },
    { text: 'ユーザー設定', icon: <SvgIcon component={SidebarUserIcon} inheritViewBox />, path: '/settings/user' },
    { text: 'LINE通知設定', icon: <SvgIcon component={SidebarLineNotifIcon} inheritViewBox />, path: '/settings/account' },
    { text: '利用明細', icon: <SvgIcon component={SidebarBillingIcon} inheritViewBox />, path: '/billing' },
    { text: '機能に関するご意見・ご要望', icon: <SvgIcon component={SidebarFeedbackIcon} inheritViewBox />, path: '/feedback' },
    { text: 'プライバシーポリシー', icon: <SvgIcon component={SidebarPrivacyIcon} inheritViewBox />, path: 'https://recrew-official.studio.site/Privacypolicy' },
    { text: '利用規約', icon: <SvgIcon component={SidebarPrivacyIcon} inheritViewBox />, path: 'https://recrew-official.studio.site/terms-business' },
    { text: 'ログアウト', icon: <LogoutIcon />, path: '__logout__' },
  ];

  const drawer = (
    <>
      {isMobile ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, ...theme.mixins.toolbar }}>
          <Box
            onClick={handleDrawerToggle}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: 'text.primary', px: 1 }}
          >
            <CloseIcon style={{ width: 36, height: 36 }} />
            <Typography sx={{ fontSize: '0.65rem', color: 'text.primary', lineHeight: 1, mt: '2px' }}>閉じる</Typography>
          </Box>
        </Box>
      ) : (
        <DrawerHeader>
          {open && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'flex-start', pl: 2 }}>
              <img src="/recrew_logo_C-3.png" alt="ReCrew Logo" style={{ height: '32px', width: 'auto' }} />
            </Box>
          )}
          <IconButton onClick={open ? handleDrawerClose : handleDrawerOpen} sx={{ color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChevronLeftIcon sx={{ fontSize: '20px', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }} />
              <ChevronLeftIcon sx={{ fontSize: '20px', ml: '-10px', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }} />
            </Box>
          </IconButton>
        </DrawerHeader>
      )}
      {!isMobile && <Divider />}

      {/* 店舗名表示 + 店舗切り替え */}
      {storeName && (
        <>
          <Box
            onClick={handleStoreMenuOpen}
            sx={{
              p: (open || isMobile) ? 1.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: (open || isMobile) ? 'flex-start' : 'center',
              gap: 1,
              cursor: hasMultipleStores ? 'pointer' : 'default',
              bgcolor: '#e8f4fd',
              borderBottom: '1px solid #d0e8f7',
              '&:hover': hasMultipleStores ? { bgcolor: '#d0e8f7' } : {},
              minHeight: 48,
            }}
          >
            <StorefrontIcon sx={{ color: BRAND_COLOR, fontSize: 20 }} />
            {(open || isMobile) && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#1a3a5c',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {storeName}
                </Typography>
                {hasMultipleStores && (
                  <KeyboardArrowDownIcon sx={{ color: '#6b8eb5', fontSize: 20 }} />
                )}
              </>
            )}
          </Box>
          <Divider />
        </>
      )}

      {/* admin権限を持つユーザーの場合、企業管理画面への切替ボタンを表示 */}
      {/* モード切替ボタン */}
      {(isManagementUser || isAdmin) && (
        <>
          <Box sx={{ p: (open || isMobile) ? 2 : 1, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
            {isManagementUser && managementAccount?.companies?.[0]?.id && (
              <Button
                variant="outlined"
                startIcon={(open || isMobile) ? <SwapHorizIcon /> : undefined}
                onClick={() => {
                  navigate(`/management/company/${managementAccount.companies[0].id}`);
                }}
                size="small"
                fullWidth={open || isMobile}
                sx={{
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  minWidth: (open || isMobile) ? undefined : 40,
                  px: (open || isMobile) ? 2 : 0,
                }}
              >
                {(open || isMobile) ? '企業管理' : <SwapHorizIcon />}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={(open || isMobile) ? <SwapHorizIcon /> : undefined}
                onClick={() => navigate('/management')}
                size="small"
                fullWidth={open || isMobile}
                sx={{
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  minWidth: (open || isMobile) ? undefined : 40,
                  px: (open || isMobile) ? 2 : 0,
                }}
              >
                {(open || isMobile) ? '運営メニュー' : <SwapHorizIcon />}
              </Button>
            )}
          </Box>
          <Divider />
        </>
      )}

      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: (open || isMobile) ? 'initial' : 'center',
                px: 2.5,
                '&:hover .MuiListItemIcon-root, &.Mui-selected .MuiListItemIcon-root': {
                  color: BRAND_COLOR,
                },
                '&:hover .MuiSvgIcon-root, &.Mui-selected .MuiSvgIcon-root': {
                  color: BRAND_COLOR,
                },
              }}
              selected={isMenuActive(item.path)}
              onClick={() => {
                if (item.path === '__logout__') {
                  handleLogout();
                } else if (item.path.startsWith('http')) {
                  window.open(item.path, '_blank', 'noopener noreferrer');
                } else {
                  navigate(item.path);
                }
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: (open || isMobile) ? 3 : 'auto',
                  justifyContent: 'center',
                  color: isMenuActive(item.path) ? BRAND_COLOR : 'inherit'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  opacity: (open || isMobile) ? 1 : 0,
                  color: isMenuActive(item.path) ? BRAND_COLOR : 'inherit'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  // サイドバーの幅を計算する関数
  const getSidebarWidth = () => {
    if (isMobile) return 0; // モバイルでは表示されない
    if (open) return drawerWidth; // 開いている場合はフル幅
    return parseInt(theme.spacing(8)); // 閉じている場合はアイコン幅（数値として返す）
  };

  // ボトムナビゲーションの高さ
  const BOTTOM_NAV_HEIGHT = 80;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {isMobile && <AppBar
        position="fixed"
        open={false}
        color="inherit"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          display: 'block',
          zIndex: mobileOpen ? theme.zIndex.drawer - 1 : theme.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ backgroundColor: 'white', minHeight: '80px !important', px: 1.5 }}>
          {/* モバイル用: ハンバーガーメニュー（アイコン+ラベル） */}
          {isMobile && (
            <Box
              onClick={handleDrawerToggle}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: 'text.primary', mr: 1, minWidth: 52, px: 1 }}
            >
              <HeaderMenuIcon style={{ width: 30, height: 30 }} />
              <Typography sx={{ fontSize: '0.65rem', color: 'text.primary', lineHeight: 1, mt: '2px', whiteSpace: 'nowrap' }}>メニュー</Typography>
            </Box>
          )}

          {/* デスクトップ用のメニュー開閉ボタン */}
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={open ? handleDrawerClose : handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, color: 'text.primary' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChevronLeftIcon sx={{ fontSize: '20px', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }} />
                <ChevronLeftIcon sx={{ fontSize: '20px', ml: '-10px', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }} />
              </Box>
            </IconButton>
          )}

          {/* PC表示時: ロゴ */}
          {!isMobile && (!open) && (
            <img
              src="/recrew_logo_C-3.png"
              alt="ReCrew Logo"
              style={{ height: '32px', width: 'auto', marginLeft: '8px' }}
            />
          )}

          {/* モバイル: 店舗名表示 */}
          {isMobile && storeName && (
            <Box
              onClick={handleStoreMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: hasMultipleStores ? 'pointer' : 'default',
                bgcolor: '#e8f4fd',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
                maxWidth: '40%',
                overflow: 'hidden',
              }}
            >
              <StorefrontIcon sx={{ color: BRAND_COLOR, fontSize: 16, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#1a3a5c',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {storeName}
              </Typography>
              {hasMultipleStores && (
                <KeyboardArrowDownIcon sx={{ color: '#6b8eb5', fontSize: 16, flexShrink: 0 }} />
              )}
            </Box>
          )}

          {/* 中央スペーサー */}
          <Box sx={{ flexGrow: 1 }} />

          {/* モバイル専用: 右側アイコン群（アイコン+ラベル） */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <Box
                onClick={() => navigate('/notifications')}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: isMenuActive('/notifications') ? BRAND_COLOR : 'text.primary', minWidth: 52, px: 1 }}
              >
                <HeaderBellIcon style={{ width: 26, height: 26 }} />
                <Typography sx={{ fontSize: '0.5rem', color: 'inherit', lineHeight: 1, mt: '2px', whiteSpace: 'nowrap' }}>お知らせ</Typography>
              </Box>
              <Box
                onClick={() => navigate('/attendance/schedule')}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: location.pathname === '/attendance/schedule' ? BRAND_COLOR : 'text.primary', minWidth: 52, px: 1 }}
              >
                <HeaderCalendarIcon style={{ width: 26, height: 26 }} />
                <Typography sx={{ fontSize: '0.5rem', color: 'inherit', lineHeight: 1, mt: '2px', whiteSpace: 'nowrap' }}>就業予定表</Typography>
              </Box>
              <Box
                onClick={() => navigate('/attendance/qr')}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: location.pathname === '/attendance/qr' ? BRAND_COLOR : 'text.primary', minWidth: 52, px: 1 }}
              >
                <HeaderQrIcon style={{ width: 26, height: 26 }} />
                <Typography sx={{ fontSize: '0.5rem', color: 'inherit', lineHeight: 1, mt: '2px', whiteSpace: 'nowrap' }}>出退勤QR</Typography>
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>}
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* デスクトップ用ドロワー */}
        {!isMobile && (
          <Drawer 
            variant="permanent" 
            open={open}
            sx={{
              '& .MuiDrawer-paper': {
                backgroundColor: 'white', // サイドバーの背景色を常に白に設定
              }
            }}
          >
            {drawer}
          </Drawer>
        )}
        
        {/* モバイル用ドロワー */}
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 320,
              backgroundColor: 'white', // モバイルドロワーの背景色
            },
          }}
        >
          {drawer}
        </MuiDrawer>
        
        {/* メインコンテンツエリア */}
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            // モバイルでは下部ナビゲーション用の余白を確保
            pb: { xs: `calc(${BOTTOM_NAV_HEIGHT}px + 50px)`, sm: 3 },
            width: `calc(100% - ${isMobile ? 0 : getSidebarWidth()}px)`,
            overflowX: 'hidden',
            mt: { xs: '80px', sm: 0 }, // モバイルのみAppBar分のオフセット
            backgroundColor: '#f8f8f9',
            minHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 64px)' },
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          {children}
        </Box>
      </Box>
      
      {/* 店舗切り替えメニュー */}
      <Menu
        anchorEl={storeMenuAnchor}
        open={Boolean(storeMenuAnchor)}
        onClose={handleStoreMenuClose}
        PaperProps={{
          sx: { minWidth: 240, maxWidth: 320 }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            店舗を切り替え
          </Typography>
        </Box>
        <Divider />
        {storeIds.map((id) => (
          <MenuItem
            key={id}
            selected={id === storeId}
            onClick={() => handleStoreSwitch(id)}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {id === storeId ? (
                <CheckIcon sx={{ color: BRAND_COLOR, fontSize: 20 }} />
              ) : (
                <StorefrontIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={storeNameMap[id] || storeName || id}
              primaryTypographyProps={{
                fontWeight: id === storeId ? 600 : 400,
                color: id === storeId ? BRAND_COLOR : 'text.primary',
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* モバイル用のボトムナビゲーション */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            height: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
            overflow: 'visible',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.12)',
            backgroundColor: 'white',
          }}
          elevation={0}
        >
          <Box sx={{ display: 'flex', height: `${BOTTOM_NAV_HEIGHT}px` }}>
            {/* ホーム */}
            <Box
              onClick={() => navigate('/')}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: '6px', cursor: 'pointer', color: location.pathname === '/' ? BRAND_COLOR : '#898989' }}
            >
              <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HomeNavIcon style={{ width: 28, height: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: '4px', color: 'inherit' }}>ホーム</Typography>
            </Box>

            {/* 求人管理 */}
            <Box
              onClick={() => navigate('/job-management')}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: '6px', cursor: 'pointer', color: ['/job-management', '/job-templates'].includes(location.pathname) ? BRAND_COLOR : '#898989' }}
            >
              <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <JobManagementNavIcon style={{ width: 28, height: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: '4px', color: 'inherit' }}>求人管理</Typography>
            </Box>

            {/* 今すぐ募集（FAB） */}
            <Box
              sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: '6px' }}
            >
              <Box sx={{ width: 30, height: 30 }} /> {/* 他メニューのアイコンと同じ高さのスペーサー */}
              <Button
                onClick={() => navigate('/line-broadcast')}
                variant="contained"
                sx={{
                  position: 'absolute',
                  bottom: '56px',
                  minWidth: 'unset',
                  width: 58,
                  height: 58,
                  borderRadius: '50%',
                  border: '3px solid white',
                  boxShadow: '0 4px 12px rgba(83,77,255,0.45)',
                  p: 0,
                  bgcolor: BRAND_COLOR,
                  '&:hover': { bgcolor: BRAND_COLOR_HOVER },
                }}
              >
                <RecruitFabIcon style={{ width: 36, height: 36, fill: 'white' }} />
              </Button>
              <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: '4px', color: location.pathname === '/line-broadcast' ? BRAND_COLOR : '#898989' }}>
                今すぐ募集
              </Typography>
            </Box>

            {/* クルー管理 */}
            <Box
              onClick={() => navigate('/crew-management')}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: '6px', cursor: 'pointer', color: (['/applicants', '/crew-management'].includes(location.pathname) || location.pathname.startsWith('/crew-management/')) ? BRAND_COLOR : '#898989' }}
            >
              <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CrewManagementNavIcon style={{ width: 28, height: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: '4px', color: 'inherit' }}>クルー管理</Typography>
            </Box>

            {/* 承認 */}
            <Box
              onClick={() => navigate('/attendance/approval')}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: '6px', cursor: 'pointer', color: location.pathname.startsWith('/attendance/approval') ? BRAND_COLOR : '#898989' }}
            >
              <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ApprovalNavIcon style={{ width: 28, height: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: '4px', color: 'inherit' }}>承認</Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Layout;